import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTr } from "@/i18n/translator";
import {
  Check, Circle, Settings, Plug, Sparkles, Calendar, BarChart3, Zap, ArrowRight, Rocket,
} from "lucide-react";

/**
 * Checklist interactivo de 6 pasos — onboarding guiado.
 * Detecta progreso real en background consultando endpoints existentes.
 */
export default function Start() {
  const tr = useTr();
  const { user } = useAuth();
  const [state, setState] = useState({
    brandConfigured: false,
    accountsConnected: 0,
    firstPostGenerated: false,
    calendarHasPost: false,
    autopilotOn: false,
    analyticsSeen: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [brands, accounts, bank, posts] = await Promise.all([
          api.get("/brands").then((r) => r.data).catch(() => []),
          api.get("/accounts").then((r) => r.data).catch(() => []),
          api.get("/bank").then((r) => r.data).catch(() => []),
          api.get("/posts").then((r) => r.data).catch(() => []),
        ]);
        const activeBrand = brands.find((b) => b.is_active) || brands[0];
        const brandOk = !!activeBrand && ((activeBrand.topics?.length || 0) > 0 || !!activeBrand.logo_url);
        setState({
          brandConfigured: brandOk,
          accountsConnected: accounts.length,
          firstPostGenerated: bank.length > 0 || posts.length > 0,
          calendarHasPost: posts.length > 0,
          autopilotOn: !!user?.autopilot_enabled,
          analyticsSeen: localStorage.getItem("cia_viewed_analytics") === "1",
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const steps = [
    {
      key: "brand",
      label: tr("Configura tu marca"),
      desc: tr("Agrega nombre, logo, colores, tono, ciudad y topics. La IA los usará para generar todo el contenido."),
      icon: Settings,
      done: state.brandConfigured,
      to: "/dashboard/brand",
      cta: tr("CONFIGURAR MARCA"),
    },
    {
      key: "accounts",
      label: tr("Conecta tus redes sociales"),
      desc: tr("Enlaza Twitter, Instagram, Facebook, LinkedIn o TikTok según tu plan."),
      icon: Plug,
      done: state.accountsConnected > 0,
      to: "/dashboard/accounts",
      cta: tr("CONECTAR REDES"),
    },
    {
      key: "generate",
      label: tr("Genera tu primer post con IA"),
      desc: tr("Prueba el generador: texto, imagen o carrusel. La IA respeta tu tono, logo y ciudad."),
      icon: Sparkles,
      done: state.firstPostGenerated,
      to: "/dashboard/generator",
      cta: tr("ABRIR GENERADOR"),
    },
    {
      key: "calendar",
      label: tr("Programa un post en el calendario"),
      desc: tr("Arrastra y suelta en el calendario o genera un plan de 30 días automático."),
      icon: Calendar,
      done: state.calendarHasPost,
      to: "/dashboard/calendar",
      cta: tr("VER CALENDARIO"),
    },
    {
      key: "autopilot",
      label: tr("Activa Autopilot") + " (Pro+)",
      desc: tr("La IA publica sola en horarios óptimos. Disponible en Pro y Agencia."),
      icon: Zap,
      done: state.autopilotOn,
      to: "/dashboard/subscription",
      cta: user?.plan === "pro" || user?.plan === "agency" ? tr("ACTIVAR AUTOPILOT") : tr("MEJORAR A PRO"),
    },
    {
      key: "analytics",
      label: tr("Revisa tus métricas"),
      desc: tr("Seguidores, impresiones, engagement y posts publicados en tiempo real."),
      icon: BarChart3,
      done: state.analyticsSeen,
      to: "/dashboard/analytics",
      cta: tr("VER ANALYTICS"),
      onClick: () => localStorage.setItem("cia_viewed_analytics", "1"),
    },
  ];

  const completed = steps.filter((s) => s.done).length;
  const pct = Math.round((completed / steps.length) * 100);

  return (
    <div className="space-y-8" data-testid="start-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ONBOARDING · INICIO GUIADO")}</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 flex items-center gap-3">
            <Rocket className="w-10 h-10 text-[#E53935]" />
            {tr("Pon tu marca en autopilot en 6 pasos")}
          </h1>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-3 max-w-2xl">
            {tr("Sigue este checklist para configurar todo de principio a fin. Puedes saltarte pasos y volver después.")}
          </p>
        </div>
        <div className="bg-[#0A0A0A] text-white p-5 min-w-[200px]">
          <div className="font-mono-mark text-[10px] text-[#FFD600]">{tr("PROGRESO")}</div>
          <div className="font-display text-5xl mt-1 font-mono-data">{pct}%</div>
          <div className="mt-3 bg-white/10 h-1">
            <div className="bg-[#FFD600] h-1 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="font-mono-mark text-[9px] text-white/70 mt-2">{completed} / {steps.length} {tr("pasos")}</div>
        </div>
      </div>

      {loading ? (
        <div className="font-mono-mark text-xs text-[#5C5C5C]">{tr("Cargando progreso…")}</div>
      ) : (
        <div className="space-y-4">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.key}
                className={`bg-white border border-[#0A0A0A] p-6 flex items-start gap-5 ${s.done ? "opacity-60" : ""}`}
                data-testid={`start-step-${s.key}`}
              >
                <div className={`w-10 h-10 shrink-0 flex items-center justify-center border-2 ${s.done ? "bg-[#E53935] border-[#E53935] text-white" : "border-[#0A0A0A] text-[#0A0A0A]"}`}>
                  {s.done ? <Check className="w-5 h-5" /> : <span className="font-display text-xl">{i + 1}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon className="w-4 h-4 text-[#E53935]" />
                    <h3 className="font-display text-xl">{s.label}</h3>
                    {s.done && <span className="font-mono-mark text-[9px] bg-[#FFD600] px-2 py-0.5">COMPLETADO</span>}
                  </div>
                  <p className="font-mono-data text-sm text-[#5C5C5C] mt-2">{s.desc}</p>
                </div>
                <Link
                  to={s.to}
                  onClick={s.onClick}
                  className="shrink-0 font-mono-mark text-[10px] bg-[#0A0A0A] text-white px-4 py-3 border border-[#0A0A0A] hover-brutal flex items-center gap-2"
                  data-testid={`start-cta-${s.key}`}
                >
                  {s.cta} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {pct === 100 && (
        <div className="border border-[#E53935] bg-[#FFD600] p-6 text-center" data-testid="start-complete-banner">
          <Check className="w-8 h-8 mx-auto text-[#E53935]" />
          <div className="font-display text-3xl mt-2">{tr("¡Tu marca está en autopilot!")}</div>
          <p className="font-mono-data text-sm mt-2">{tr("Ya configuraste todo. Revisa el resumen y sigue generando contenido.")}</p>
        </div>
      )}
    </div>
  );
}
